import React, { useState, useEffect } from 'react';
import { Autocomplete, Box, Typography, FormControl, Select, MenuItem, TextField, Checkbox, Grid, Chip } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const ServiceSelectionComponent = ({ serviceList, categoryList, subCategoryList }) => {
  const { control, watch, setValue, setError } = useFormContext();
  const [childServices, setChildServices] = useState([]);
  const servicesMasterId = watch('service');
  const categoryId = watch('serviceCategoryId');
  const subCategoryId = watch('subCategoryId');
  const services = watch('serviceRelations') || [];

  useEffect(() => {
    if (servicesMasterId) {
      const selectedService = serviceList.find(serv => serv.masterServiceId === servicesMasterId);
      const allChildServices = selectedService?.childServices || [];      
      setChildServices(allChildServices);
    } else {
      setChildServices([]);
    }
  }, [servicesMasterId, serviceList]);

  const handleServiceSelection = (newValue) => {
    // Get existing services for the current master service (to preserve their data)
    const existingServicesForMaster = services.filter(service => 
      service.servicesMasterId === servicesMasterId
    );
    
    // Get services from other master services (keep them unchanged)
    const servicesFromOtherMasters = services.filter(service => 
      service.servicesMasterId !== servicesMasterId
    );

    // Process the new selections
    const selectedChildServices = newValue.map((service, index) => {
      // Check if this specific service already exists in our current services
      const existingService = existingServicesForMaster.find(s => 
        s.servicesChildId === service.childServiceId
      );

      // If it exists, preserve all its data including ServiceBrands
      if (existingService) {
        return existingService;
      }

      // If it's new, create with default values
      const newService = {
        servicesMasterId: servicesMasterId,
        servicesChildId: service.childServiceId,
        categoryId: categoryId,
        subCategoryId: subCategoryId,
        categoryTitle: categoryList.find(cat => cat.id === categoryId)?.title,
        subCategoryTitle: subCategoryList.find(subCat => subCat.id === subCategoryId)?.title,
        masterServiceTitle: serviceList.find(s => s.masterServiceId === servicesMasterId)?.title,
        childServiceTitle: service.title,
        // ServiceBrands: service.ServiceBrands || [], // Keep existing ServiceBrands if any
        // availableBrands: getAvailableBrandsForService(service.childServiceId) // Store available brands for this service
      };
      
      // Set validation error for new services that don't have brands selected
      setTimeout(() => {
        const newServiceIndex = servicesFromOtherMasters.length + index;
        setError(`serviceRelations.${newServiceIndex}.brands`, {
          type: 'required',
          message: 'At least one variation must be selected'
        });
      }, 0);
      
      return newService;
    });
      
    // Combine services from other masters with the updated services for current master
    const updatedServices = [...servicesFromOtherMasters, ...selectedChildServices];
    
    setValue('serviceRelations', updatedServices);
  };

  const getSelectedChildServices = () => {
    return services
      .filter(service => service.servicesMasterId === servicesMasterId)
      .map(service => childServices.find(cs => cs.childServiceId === service.servicesChildId))
      .filter(Boolean);
  };

  return (
    <>
      <Grid item xs={12} sm={6}>
        <Typography sx={{fontSize:'0.75rem', fontWeight: 600}}>Services</Typography>
        <Controller
          name='service'
          control={control}
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth>
              <Select
                {...field}
                size='small'
                sx={{
                  fontSize: '0.75rem',
                  '& .MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
                error={!!error}
                disabled={!watch('subCategoryId')}
              >
                {serviceList.map((service) => (
                  <MenuItem sx={{fontSize:'0.75rem'}} key={service.masterServiceId} value={service.masterServiceId}>
                    {service.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Typography sx={{fontSize:'0.75rem', fontWeight: 600}}>Child Services</Typography>
        <Controller
          name='serviceRelations'
          control={control}
          rules={{}}
          render={({ field }) => (
            <Autocomplete
              {...field}
              sx={{
                '& .MuiAutocomplete-input': {
                  fontSize: '0.75rem',
                },
              }}
              disabled={!childServices.length > 0}
              multiple
              disableCloseOnSelect
              options={childServices}
              getOptionLabel={(option) => option.title}
              renderOption={(props, option, { selected }) => {
                return (
                  <li {...props}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ 
                        marginRight: 8,
                        transform: 'scale(0.8)',
                      }}
                      checked={selected}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                        {option.title}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size='small'
                  variant="outlined"
                  placeholder="Select services"
                />
              )}
              onChange={(_, newValue) => {
                handleServiceSelection(newValue);
              }}
              isOptionEqualToValue={(option, value) => option.childServiceId === value.childServiceId}
              value={getSelectedChildServices()}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.childServiceId}
                    label={option.title}
                    size="small"
                    sx={{ fontSize: '0.7rem', maxWidth: 160 }}
                  />
                ))
              }
            />
          )}
        />
        {childServices.length === 0 && servicesMasterId && (
          <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', fontStyle: 'italic', mt: 0.5 }}>
            No child services for this service.
          </Typography>
        )}
      </Grid>
    </>
  );
};

export default ServiceSelectionComponent;
